import type { FC } from 'react';
import { AILabel, AILabelActions, AILabelContent } from '@carbon/react';

import type { SobreAIA } from '../../../logica/nyta/explicacoes';
import styles from './ia.module.scss';

export type { SobreAIA };

// O RÓTULO DE IA (o `AILabel` do Carbon for AI), em português e com o conteúdo de explicação no
// molde do Carbon: o que é, os detalhes que sustentam, de onde veio e o que se pode fazer.
//
// ⚠️ O RÓTULO MARCA SÓ O QUE A IA GEROU. O diagnóstico REAL é do motor (nota, selo, retrato e
// leitura são regra e texto fixo), e o plano é do método: pôr "IA" neles seria mentir para quem
// lê. A Nyta escreve com um modelo de linguagem, e é isso que o rótulo aponta.
// ⚠️ Nunca como enfeite nem como botão de ação ("gerar de novo" tem o próprio ícone).
// ⚠️ Com `reverter`, quem editou o que a IA sugeriu vê o botão de voltar à sugestão no lugar do
// rótulo (e o estilo de IA sai do componente: é o padrão do Carbon).
//
// `uiCarbon.test.ts` só deixa o `AILabel` aparecer aqui.

export const RotuloDeIA: FC<{
  sobre: SobreAIA;
  /** `inline` só junto de texto corrido; o padrão vai no canto dos contêineres e dos campos. */
  inline?: boolean;
  tamanho?: 'mini' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** O texto ao lado do "IA" no rótulo em linha ("Nyta"). */
  texto?: string;
  /** A pessoa editou a sugestão: o rótulo vira "voltar à sugestão da Nyta". */
  reverter?: () => void;
  alinhar?: 'bottom-end' | 'bottom-start' | 'bottom' | 'top' | 'left' | 'right' | 'top-end' | 'top-start';
}> = ({ sobre, inline, tamanho, texto, reverter, alinhar }) => (
  <AILabel
    aiText='IA'
    // O Carbon monta o nome do botão com `slugLabel` (padrão "Show information"), e não com o
    // `aria-label`: sem ele, o leitor de tela lia "IA Show information".
    slugLabel='Como a Nyta gerou isto'
    aria-label='Como a Nyta gerou isto'
    revertLabel='Voltar à sugestão da Nyta'
    revertActive={!!reverter}
    onRevertClick={reverter}
    kind={inline ? 'inline' : 'default'}
    size={tamanho ?? (inline ? 'md' : 'xs')}
    textLabel={inline ? texto : undefined}
    align={alinhar}
    autoAlign
  >
    <AILabelContent>
      <div className={styles.explicacao}>
        <p className={styles.quem}>Nyta · a IA da Maestra</p>
        <p className={styles.tituloDaExplicacao}>{sobre.titulo}</p>
        <p className={styles.textoDaExplicacao}>{sobre.texto}</p>
        {sobre.detalhes && sobre.detalhes.length > 0 && (
          <dl className={styles.detalhes}>
            {sobre.detalhes.map((d) => (
              <div key={d.rotulo} className={styles.detalhe}>
                <dt>{d.rotulo}</dt>
                <dd>{d.valor}</dd>
              </div>
            ))}
          </dl>
        )}
        {sobre.fontes && sobre.fontes.length > 0 && (
          <div className={styles.fontes}>
            <p className={styles.quem}>Fontes</p>
            <ul>{sobre.fontes.map((f) => <li key={f}>{f}</li>)}</ul>
          </div>
        )}
      </div>
      {sobre.acoes && <AILabelActions>{sobre.acoes}</AILabelActions>}
    </AILabelContent>
  </AILabel>
);
